/* global document, CustomEvent */
/**
 * product-buy-buttons-sync.js
 *
 * Keeps multiple buy-buttons blocks (and sticky add-to-cart) on the same product page
 * in sync for variant and quantity. Uses document-level CustomEvents so blocks do not
 * depend on DOM order or a single instance.
 *
 * Why it works:
 * - One source of truth: variant picker dispatches variant:update; quantity selector
 *   dispatches quantity-selector:update. We bridge these to product:variant-change and
 *   product:quantity-change on document so any consumer (buy-buttons, sticky ATC) can listen.
 * - Each buy-buttons block keeps its own form and unique form ID (no duplicate IDs).
 * - Sync only updates input values and disabled state; form submission stays native.
 *
 * Idempotent: safe to run multiple times (e.g. when multiple blocks load the script).
 */
(function () {
  'use strict';

  var PRODUCT_VARIANT_CHANGE = 'product:variant-change';
  var PRODUCT_QUANTITY_CHANGE = 'product:quantity-change';
  var THEME_VARIANT_UPDATE = 'variant:update';
  var THEME_QUANTITY_UPDATE = 'quantity-selector:update';
  if (document.documentElement.dataset.productBuyButtonsSync === '1') return;
  document.documentElement.dataset.productBuyButtonsSync = '1';

  function getProductIdFromBlock(block) {
    return block && block.getAttribute && block.getAttribute('data-product-id');
  }

  function getVariantInput(form) {
    return form && form.querySelector && form.querySelector('input[name="id"]');
  }

  function getQuantityFallback(form) {
    return form && form.querySelector && form.querySelector('input[data-buy-buttons-quantity-fallback]');
  }

  function getQuantitySelector(form) {
    var comp = form && form.querySelector && form.querySelector('quantity-selector-component');
    return comp && typeof comp.setValue === 'function' && typeof comp.updateButtonStates === 'function' ? comp : null;
  }

  function getAddToCartContainer(form) {
    return form && form.closest && form.closest('product-form-component');
  }

  function getAddToCartButton(container) {
    if (!container || !container.querySelector) return null;
    var btn = container.querySelector('[ref="addToCartButton"]');
    return btn && btn.tagName === 'BUTTON' ? btn : null;
  }

  /**
   * When a form has a quantity-selector-component, we must not submit two "quantity" inputs.
   * Remove name from the fallback so only the visible quantity input submits.
   */
  function ensureSingleQuantityInput(form) {
    var fallback = getQuantityFallback(form);
    var selector = getQuantitySelector(form);
    if (!fallback) return;
    if (selector) {
      fallback.removeAttribute('name');
    } else {
      if (!fallback.hasAttribute('name')) fallback.setAttribute('name', 'quantity');
    }
  }

  function onVariantChange(evt) {
    var detail = evt.detail;
    if (!detail) return;
    var productId = detail.productId;
    var variantId = detail.variantId;
    var available = detail.available;

    var blocks = document.querySelectorAll('[data-buy-buttons][data-product-id]');
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      if (productId && getProductIdFromBlock(block) !== productId) continue;

      var form = block.querySelector && block.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) continue;

      var idInput = getVariantInput(form);
      if (idInput && variantId != null && variantId !== '') {
        idInput.value = String(variantId);
      }

      var container = getAddToCartContainer(form);
      var btn = getAddToCartButton(container);
      if (btn !== null && available !== undefined) {
        btn.disabled = !available;
      }

      var kickflipTriggers = form.querySelectorAll('[data-kickflip-customize][data-sync-variant-availability="true"]');
      for (var ki = 0; ki < kickflipTriggers.length; ki++) {
        var kt = kickflipTriggers[ki];
        if (available === undefined) continue;
        if (kt.tagName === 'BUTTON') {
          kt.disabled = !available;
        } else {
          kt.setAttribute('aria-disabled', available ? 'false' : 'true');
        }
      }

      if (detail.addToCartText && btn) {
        var textEl = btn.querySelector && btn.querySelector('.add-to-cart-text');
        // if (textEl) {
        //   var contentEl = textEl.querySelector && textEl.querySelector('.add-to-cart-text__content');
        //   if (contentEl) {
        //     contentEl.textContent = detail.addToCartText;
        //   } else {
        //     textEl.textContent = detail.addToCartText;
        //   }
        // }
      }
    }
  }

  function onQuantityChange(evt) {
    var detail = evt.detail;
    if (!detail || detail.quantity === undefined) return;
    var quantity = Number(detail.quantity);
    if (isNaN(quantity) || quantity < 1) quantity = 1;
    var productId = detail.productId;

    var blocks = document.querySelectorAll('[data-buy-buttons][data-product-id]');
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      if (productId && getProductIdFromBlock(block) !== productId) continue;

      var form = block.querySelector && block.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) continue;

      var selector = getQuantitySelector(form);
      if (selector) {
        selector.setValue(String(quantity));
        selector.updateButtonStates();
      } else {
        var fallback = getQuantityFallback(form);
        if (fallback) fallback.value = String(quantity);
      }
    }
  }

  /**
   * Bridge theme variant:update (bubbles from section) to document product:variant-change.
   * Theme passes variant as event.detail.resource (from variant-picker JSON). Support
   * both resource.id and resource as number/string for resilience.
   */
  function bridgeVariantUpdate(evt) {
    var d = evt.detail;
    if (!d) return;
    var resource = d.resource;
    var data = d.data || {};
    var productId = (data.productId != null && data.productId !== '') ? data.productId : null;
    var variantId = null;
    if (resource != null) {
      if (typeof resource === 'object' && resource.id != null) variantId = resource.id;
      else if (typeof resource === 'number' || typeof resource === 'string') variantId = resource;
    }
    var available = resource && typeof resource === 'object' && typeof resource.available === 'boolean' ? resource.available : undefined;
    var addToCartText = '';
    if (data.html) {
      var btn = data.html.querySelector && data.html.querySelector('product-form-component [ref="addToCartButton"]');
      if (btn) {
        // var textWrapper = btn.querySelector && btn.querySelector('.add-to-cart-text');
        // if (textWrapper) {
        //   var contentEl = textWrapper.querySelector && textWrapper.querySelector('.add-to-cart-text__content');
        //   addToCartText = (contentEl ? contentEl.textContent : textWrapper.textContent) || '';
        // }
      }
    }
    document.dispatchEvent(new CustomEvent(PRODUCT_VARIANT_CHANGE, {
      detail: { variantId: variantId, productId: productId, available: available, addToCartText: addToCartText }
    }));
  }

  /**
   * Bridge quantity-selector:update to document product:quantity-change only for product
   * page (not cart line). cartLine is set for cart drawer quantity changes.
   */
  function bridgeQuantityUpdate(evt) {
    if (!evt.detail || evt.detail.cartLine !== undefined) return;
    var quantity = evt.detail.quantity;
    var form = evt.target && evt.target.closest && evt.target.closest('form');
    var productId = null;
    if (form) {
      var comp = form.closest && form.closest('product-form-component');
      if (comp && comp.dataset && comp.dataset.productId) productId = comp.dataset.productId;
    }
    document.dispatchEvent(new CustomEvent(PRODUCT_QUANTITY_CHANGE, {
      detail: { quantity: quantity, productId: productId }
    }));
  }

  function init() {
    document.addEventListener(PRODUCT_VARIANT_CHANGE, onVariantChange);
    document.addEventListener(PRODUCT_QUANTITY_CHANGE, onQuantityChange);
    document.addEventListener(THEME_VARIANT_UPDATE, bridgeVariantUpdate);
    document.addEventListener(THEME_QUANTITY_UPDATE, bridgeQuantityUpdate);

    var forms = document.querySelectorAll('form[data-type="add-to-cart-form"]');
    for (var j = 0; j < forms.length; j++) ensureSingleQuantityInput(forms[j]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
